/* eslint-disable import/prefer-default-export */
import { NONE, TABLE_FIELDS, TRAN_TYPES_DATA } from '../constants';
import { PROCESS_MODES_NAMES } from '../../utils';
import { MAP_FORMAT } from '../utils.mapFormats';

const ERRORS = (locale) => ({
  REQUIRED_ACCOUNT_TYPE_AND_TRAN_TYPE: locale['wireImports.specifyAccountTypeAndTranType'],
  REQUIRED_ACCOUNT_TYPE: locale['wireImports.specifyAccountType'],
  REQUIRED_TRAN_TYPE: locale['wireImports.specifyTranType'],
  SPECIFY_VALUE_FOR_TRAN_TYPE: locale['wireImports.specifyValueForTranType'],
  SPECIFY_VALUE_FOR_ACCOUNT_TYPE: locale['wireImports.specifyValueForAccountType'],
  SPECIFY_POSITION_FOR_TRAN_TYPE: locale['wireImports.specifyPositionForTranType'],
  SPECIFY_POSITION_FOR_ACCOUNT_TYPE: locale['wireImports.specifyPositionForAccountType']
});

const requiredTransactionTypeRow = ({
  VALIDATION_ERRORS,
  fields: {
    emptyAccountTypeAddValue,
    emptyAccountTypePosition,
    emptyTransactionTypeAddValue,
    emptyTransactionTypePosition,
    hasAccountTypeValues,
    hasCheckedAccountCheckbox,
    hasCheckedTranTypeCheckbox,
    hasTransactionTypeValues
  },
  processMode
}) => {
  const emptyAccountType =
    emptyAccountTypePosition && emptyAccountTypeAddValue && !hasAccountTypeValues;
  const emptyTranType =
    emptyTransactionTypePosition && emptyTransactionTypeAddValue && !hasTransactionTypeValues;
  if (emptyTranType && emptyAccountType && processMode !== PROCESS_MODES_NAMES.UPDATE_ONLY) {
    return [VALIDATION_ERRORS.REQUIRED_ACCOUNT_TYPE_AND_TRAN_TYPE];
  }

  // UPDATE ONLY
  if (
    processMode === PROCESS_MODES_NAMES.UPDATE_ONLY &&
    hasCheckedAccountCheckbox &&
    emptyAccountType
  ) {
    return [VALIDATION_ERRORS.REQUIRED_ACCOUNT_TYPE];
  }

  // UPDATE ONLY
  if (
    processMode === PROCESS_MODES_NAMES.UPDATE_ONLY &&
    hasCheckedTranTypeCheckbox &&
    emptyTranType
  ) {
    return [VALIDATION_ERRORS.REQUIRED_TRAN_TYPE];
  }

  if (processMode !== PROCESS_MODES_NAMES.UPDATE_ONLY && emptyAccountType) {
    return [VALIDATION_ERRORS.REQUIRED_ACCOUNT_TYPE];
  }

  if (processMode !== PROCESS_MODES_NAMES.UPDATE_ONLY && emptyTranType) {
    return [VALIDATION_ERRORS.REQUIRED_TRAN_TYPE];
  }

  return [];
};

const atLeastOneValue = ({
  VALIDATION_ERRORS,
  fields: {
    emptyAccountTypePosition,
    emptyTransactionTypePosition,
    hasAccountTypeValues,
    hasTransactionTypeValues
  }
}) => {
  const errors = [];

  if (!emptyTransactionTypePosition && !hasTransactionTypeValues) {
    errors.push(VALIDATION_ERRORS.SPECIFY_VALUE_FOR_TRAN_TYPE);
  }

  if (!emptyAccountTypePosition && !hasAccountTypeValues) {
    errors.push(VALIDATION_ERRORS.SPECIFY_VALUE_FOR_ACCOUNT_TYPE);
  }

  return errors;
};

const positionInImportFile = ({
  VALIDATION_ERRORS,
  fields: {
    emptyAccountTypePosition,
    emptyTransactionTypePosition,
    hasAccountTypeValues,
    hasTransactionTypeValues
  }
}) => {
  const errors = [];

  if (emptyTransactionTypePosition && hasTransactionTypeValues) {
    errors.push(VALIDATION_ERRORS.SPECIFY_POSITION_FOR_TRAN_TYPE);
  }

  if (emptyAccountTypePosition && hasAccountTypeValues) {
    errors.push(VALIDATION_ERRORS.SPECIFY_POSITION_FOR_ACCOUNT_TYPE);
  }

  return errors;
};

const createErrors = (errorNames) => errorNames.map((error) => ({ code: error, message: error }));

export const validateTransactionTypeRow = ({
  accountTypesData,
  formState,
  locale,
  mapFormat,
  processMode,
  setFormErrors
}) => {
  const VALIDATION_ERRORS = ERRORS(locale);
  const hasCheckedAccountCheckbox =
    formState[`${TABLE_FIELDS.ACCOUNT_TYPE}_match`] ||
    formState[`${TABLE_FIELDS.ACCOUNT_TYPE}_update`];
  const hasCheckedTranTypeCheckbox =
    formState[`${TABLE_FIELDS.TRAN_TYPE}_match`] || formState[`${TABLE_FIELDS.TRAN_TYPE}_update`];

  if (
    processMode === PROCESS_MODES_NAMES.UPDATE_ONLY &&
    !formState[`${TABLE_FIELDS.ACCOUNT_TYPE}_match`] &&
    !formState[`${TABLE_FIELDS.ACCOUNT_TYPE}_update`] &&
    !formState[`${TABLE_FIELDS.TRAN_TYPE}_match`] &&
    !formState[`${TABLE_FIELDS.TRAN_TYPE}_update`]
  ) {
    setFormErrors((prevState) =>
      prevState.filter((error) => !Object.values(VALIDATION_ERRORS).includes(error.code))
    );
    return true;
  }

  const transactionTypePosition =
    mapFormat === MAP_FORMAT.DELIMITED
      ? TABLE_FIELDS.TRAN_TYPE
      : `${TABLE_FIELDS.TRAN_TYPE}_startPosition`;
  const transactionTypeAddValue = `${TABLE_FIELDS.TRAN_TYPE}_addValue`;

  const accountTypePosition =
    mapFormat === MAP_FORMAT.DELIMITED
      ? TABLE_FIELDS.ACCOUNT_TYPE
      : `${TABLE_FIELDS.ACCOUNT_TYPE}_startPosition`;
  const accountTypeAddValue = `${TABLE_FIELDS.ACCOUNT_TYPE}_addValue`;

  const emptyTransactionTypeAddValue =
    !formState[transactionTypeAddValue] || formState[transactionTypeAddValue]?.[0]?.id === NONE;
  const emptyAccountTypeAddValue =
    !formState[accountTypeAddValue] || formState[accountTypeAddValue]?.[0]?.id === NONE;

  const emptyTransactionTypePosition = !formState[transactionTypePosition];
  const emptyAccountTypePosition = !formState[accountTypePosition];

  const hasTransactionTypeValues = TRAN_TYPES_DATA.find((item) => formState[item.id]);
  const hasAccountTypeValues = accountTypesData.find((item) => formState[item.id]);

  const fields = {
    emptyTransactionTypeAddValue,
    emptyAccountTypeAddValue,
    emptyTransactionTypePosition,
    emptyAccountTypePosition,
    hasTransactionTypeValues,
    hasAccountTypeValues,
    hasCheckedAccountCheckbox,
    hasCheckedTranTypeCheckbox
  };

  const required = requiredTransactionTypeRow({
    formState,
    fields,
    accountTypesData,
    VALIDATION_ERRORS,
    processMode
  });
  const value =
    mapFormat === MAP_FORMAT.DELIMITED
      ? atLeastOneValue({ formState, fields, accountTypesData, VALIDATION_ERRORS })
      : [];
  const importFile =
    mapFormat === MAP_FORMAT.DELIMITED
      ? positionInImportFile({
          formState,
          fields,
          accountTypesData,
          VALIDATION_ERRORS
        })
      : [];

  const errorNames = [...required, ...value, ...importFile];
  const errorsList = createErrors(errorNames);

  setFormErrors(errorsList);

  return !errorsList.length;
};
